/**
 * HTML to PDF のオプション
 */
export type HtmlToPdfOptions = {
  /**
   * HTML 要素を指定します。
   */
  htmlElement?: HTMLElement
  /**
   * HTML 要素のセレクターを指定します。
   */
  htmlElementSelector?: string
  /**
   * HTML の内容を直接指定します。
   */
  htmlContent?: string
  /**
   * ダウンロード時のファイル名を指定します。
   */
  filename?: string
  /**
   * 既存ページ内の CSS スタイルを収集しないようにします。
   * @default false
   */
  disableCollectCSSStyles?: boolean
  /**
   * アイコンフォントを読み込まないようにするには true を指定します。
   * ペイロードサイズを小さくするために使用します。
   * @default false
   */
  disableLoadIconFont?: boolean
  /**
   * ダウンロードを無効にします。
   * @default false
   */
  disableDownload?: boolean
  /**
   * 追加のCSSスタイルを指定します。
   */
  additionalStyles?: string
  /**
   * 拡大率を指定します
   * @default 1.0
   */
  zoom?: number
  /**
   * 用紙の幅を指定します。単位は pt, px, in, mm, cm, pc が使用できます。
   * 通常は レンダリングされる領域の幅を px で指定します。
   * 例: "1080px", "210mm"
   * @default 1080px
   */
  paperWidth?: string
  /**
   * 用紙の高さを指定します。単位は pt, px, in, mm, cm, pc が使用できます。
   * 高さを指定しない場合は 幅の 1.4142 倍の高さになります。
   * 例: "1543px", "297mm"
   * @default 1543px
   */
  paperHeight?: string
  /**
   * singlePage が true の場合、コンテンツ全体を 1 ページに収めます。
   * true に設定すると、paperHeight と nativePageRanges の値は無視されます。
   * @default false
   */
  singlePage?: boolean
  /**
   * 上マージンの幅を指定します。単位は %, pt, px, in, mm, cm, pc が使用できます。
   * デフォルトでは 用紙幅の 2.4% になります。
   * @default '2.4%'
   */
  marginTop?: string
  /**
   * 下マージンの幅を指定します。単位は %, pt, px, in, mm, cm, pc が使用できます。
   * デフォルトでは 用紙幅の 2.4% になります。
   * @default '2.4%'
   */
  marginBottom?: string
  /**
   * 左マージンの幅を指定します。単位は %, pt, px, in, mm, cm, pc が使用できます。
   * デフォルトでは 用紙幅の 2.4% になります。
   * @default '2.4%'
   */
  marginLeft?: string
  /**
   * 右マージンの幅を指定します。単位は %, pt, px, in, mm, cm, pc が使用できます。
   * デフォルトでは 用紙幅の 2.4% になります。
   * @default '2.4%'
   */
  marginRight?: string
  /**
   * CSS で定義されたページサイズを優先するかどうかを指定します。
   * @default false
   */
  preferCssPageSize?: boolean
  /**
   * ドキュメントのアウトラインを PDF に埋め込むかどうかを指定します。
   * @default false
   */
  generateDocumentOutline?: boolean
  /**
   * 背景グラフィックを印刷するかどうかを指定します。
   * @default true
   */
  printBackground?: boolean
  /**
   * デフォルトの白い背景を非表示にし、透明な PDF を生成するかどうかを指定します。
   * @default false
   */
  omitBackground?: boolean
  /**
   * 用紙の向きを横向きに設定します。
   * @default false
   */
  landscape?: boolean
  /**
   * ページレンダリングのスケールを指定します。
   * @default 1.0
   */
  scale?: number
  /**
   * 印刷するページの範囲を指定します。例: "1-5, 8, 11-13"。空の場合はすべてのページを意味します。
   * @default All pages
   */
  nativePageRanges?: string
  /**
   * ヘッダーとして使用する HTML ファイルを指定します。
   * 完全な HTML ドキュメントである必要があります。
   */
  header?: string
  /**
   * フッターとして使用する HTML ファイルを指定します。
   * 完全な HTML ドキュメントである必要があります。
   */
  footer?: string
  /**
   * HTML ドキュメントを PDF に変換する前に待機する時間を指定します。
   * 例: "5s" (5秒)。
   */
  waitDelay?: string
  /**
   * PDF に変換する前に待機する JavaScript 式を指定します。
   * 式が true を返すまで待機します。
   */
  waitForExpression?: string
  /**
   * エミュレートするメディアタイプを指定します。"screen" または "print" を指定できます。
   * @default print
   */
  emulatedMediaType?: 'screen' | 'print'
  /**
   * Chromium クッキーjar に保存するクッキーを JSON 形式で指定します。
   */
  cookies?: {
    name: string
    value: string
    domain: string
    path?: string
    secure?: boolean
    httpOnly?: boolean
    sameSite?: 'Strict' | 'Lax' | 'None'
  }[]
  /**
   * デフォルトの User-Agent HTTP ヘッダーを上書きします。
   */
  userAgent?: string
  /**
   * Chromium によって送信される追加の HTTP ヘッダーを JSON 形式で指定します。
   */
  extraHttpHeaders?: Record<string, string>
  /**
   * メインページの HTTP ステータスコードが許容できない場合に、409 Conflict レスポンスを返します。
   * @default [499, 599]
   */
  failOnHttpStatusCodes?: number[]
  /**
   * 少なくとも 1 つのリソースの HTTP ステータスコードが許容できない場合に、409 Conflict レスポンスを返します。
   */
  failOnResourceHttpStatusCodes?: number[]
  /**
   * Chromium が少なくとも 1 つのリソースのロードに失敗した場合に、409 Conflict レスポンスを返します。
   * @default false
   */
  failOnResourceLoadingFailed?: boolean
  /**
   * Chromium コンソールに例外がある場合に、409 Conflict レスポンスを返します。
   * @default false
   */
  failOnConsoleExceptions?: boolean
  /**
   * Chromium ネットワークがアイドル状態になるのを待たないようにします。
   * @default true
   */
  skipNetworkIdleEvent?: boolean
  /**
   * ファイルの分割モードを指定します。"intervals" または "pages" を指定できます。
   * - intervals: ページを指定された間隔で分割します。splitSpan で間隔を指定する必要があります。
   * - pages: 1ページごとにファイルを作成します。
   * @default 'pages'
   */
  splitMode?: 'intervals' | 'pages'
  /**
   * 抽出する間隔またはページ範囲を指定します。splitMode に応じて異なります。
   */
  splitSpan?: string
  /**
   * 抽出されたページを単一のファイルに入れるか、ページ範囲の数だけファイルを作成するかを指定します。
   * pages モードでのみ機能します。
   * @default false
   */
  splitUnify?: boolean
  /**
   * 結果の PDF を指定された PDF/A 形式に変換します。
   */
  pdfa?: 'PDF/A-1b' | 'PDF/A-2b' | 'PDF/A-3b'
  /**
   * 最適なアクセシビリティのために、PDF for Universal Access を有効にします。
   * @default false
   */
  pdfua?: boolean
  /**
   * 書き込むメタデータを JSON 形式で指定します。
   */
  metadata?: Record<string, any>
}

const gotenbergRequestOptionKeys = [
  'paperWidth',
  'paperHeight',
  'singlePage',
  'marginTop',
  'marginBottom',
  'marginLeft',
  'marginRight',
  'preferCssPageSize',
  'generateDocumentOutline',
  'printBackground',
  'omitBackground',
  'landscape',
  'scale',
  'nativePageRanges',
  'userAgent',
  'extraHttpHeaders',
  'failOnHttpStatusCodes',
  'failOnResourceHttpStatusCodes',
  'failOnResourceLoadingFailed',
  'failOnConsoleExceptions',
  'skipNetworkIdleEvent',
  'splitMode',
  'splitSpan',
  'splitUnify',
  'pdfa',
  'pdfua',
  'metadata',
  'waitDelay',
  'waitForExpression',
  'emulatedMediaType',
  'cookies',
  'userAgent',

  // 'header', // files の 一部として 設定される
  // 'footer', // files の 一部として 設定される
]

const heightRatioDefault = 1.4142

const calcHeightFromWidth = (width) => {
  return calcSizeWithRatio(width, heightRatioDefault)
}

const calcSizeWithRatio = (size: string, ratio: number) => {
  const sizeNum = parseFloat(size)
  const sizeUnit = size.replace(sizeNum.toString(), '')
  return Math.round(sizeNum * ratio).toString() + sizeUnit
}

const gotenbergApiUrl =
  process.env.GOTENBERG_API_URL ||
  'https://gotenberg-nginx-890878645232.asia-northeast1.run.app'

const loadLineAwsomeFontCSSAsDataUrl = async () => {
  const response = await fetch(
    'https://maxst.icons8.com/vue-static/landings/line-awesome/line-awesome/1.3.0/fonts/la-solid-900.woff2',
  )
  const blob = await response.blob()
  const reader = new FileReader()
  const dataUrl = await new Promise((resolve) => {
    reader.onload = () => {
      const dataUrl = reader.result as string
      resolve(dataUrl)
    }
    reader.readAsDataURL(blob)
  })
  return `
@font-face {
  font-family: 'Line Awesome Free';
  src: url(${dataUrl});
}`
}

async function getCSSRulesFromExternalCSSUrl(
  styleSheetUrl: string,
): Promise<string | null> {
  try {
    const response = await fetch(styleSheetUrl)
    const cssText = await response.text()
    return cssText
  } catch (error) {
    console.error('Error fetching CSS:', error)
    return null
  }
}

const getStyleSheetsInDocumentOrder = () => {
  const sheets = Array.from(document.styleSheets)

  // DOMでの出現順序を維持するため、ownerNodeを使用して並び替え
  return sheets.sort((a, b) => {
    const aNode = a.ownerNode
    const bNode = b.ownerNode

    // ownerNodeがない場合は後ろに
    if (!aNode || !bNode) return 1

    // document内での位置を比較
    return Node.prototype.compareDocumentPosition.call(aNode, bNode) &
      Node.DOCUMENT_POSITION_FOLLOWING
      ? -1
      : 1
  })
}

/**
 * Gotenberg クライアント for HTML to PDF
 */
class GotenbergClient {
  apiUrl: string
  options: HtmlToPdfOptions
  constructor(options: HtmlToPdfOptions, apiUrl = gotenbergApiUrl) {
    // width, height が 何も 指定されていなければ デフォルト値 を設定
    if (!options.paperWidth) {
      options.paperWidth = '1080px'
    }
    const width = options.paperWidth
    if (!options.paperHeight) {
      options.paperHeight = calcHeightFromWidth(options.paperWidth)
    }
    const defaultOptions: HtmlToPdfOptions = {
      marginTop: calcSizeWithRatio(width, 0.024),
      marginBottom: calcSizeWithRatio(width, 0.024),
      marginLeft: calcSizeWithRatio(width, 0.024),
      marginRight: calcSizeWithRatio(width, 0.024),
      printBackground: true,
      scale: 1.0,
      landscape: false,
    }
    this.options = { ...defaultOptions, ...options }
    // margin 系が % で指定されているなら 値を計算する
    const marginKeys = ['marginTop', 'marginBottom', 'marginLeft', 'marginRight']
    marginKeys.forEach((key) => {
      if (this.options[key] && this.options[key].endsWith('%')) {
        const ratio = parseFloat(this.options[key].replace('%', '')) / 100
        this.options[key] = calcSizeWithRatio(this.options.paperWidth, ratio)
      }
    })
    this.apiUrl = apiUrl
  }

  /**
   * 特定の要素をPDF化
   * @param {HTMLElement} element - PDF化する要素
   * @param {Object} options - PDF生成オプション
   */
  public async run(): Promise<Blob> {
    const indexHtml = await this.resolveHtmlContent()

    const formData = new FormData()
    formData.append('files', new Blob([indexHtml], { type: 'text/html' }), 'index.html')
    if (this.options.header) {
      formData.append(
        'files',
        new Blob([this.options.header], { type: 'text/html' }),
        'header.html',
      )
    }
    if (this.options.footer) {
      formData.append(
        'files',
        new Blob([this.options.footer], { type: 'text/html' }),
        'footer.html',
      )
    }

    Object.entries(this.options).forEach(([key, value]) => {
      if (gotenbergRequestOptionKeys.includes(key)) {
        formData.append(key, value.toString())
      }
    })

    try {
      const response = await fetch(`${this.apiUrl}/forms/chromium/convert/html`, {
        method: 'POST',
        mode: 'cors', // CORSモードを明示的に指定
        credentials: 'omit', // クレデンシャルは送信しない
        headers: {
          // Accept: 'application/pdf', // PDFを期待することを明示
          // 'Content-Type': 'multipart/form-data',
        },
        body: formData,
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
      }

      // レスポンスタイプをチェック
      const contentType = response.headers.get('content-type')
      const allowedContentTypes = ['application/pdf', 'application/zip']
      if (!contentType || !allowedContentTypes.includes(contentType)) {
        throw new Error(`Expected PDF or ZIP but got ${contentType}`)
      }

      // BlobとしてPDFを取得
      const pdfBlob = await response.blob()
      if (this.options.disableDownload !== true) {
        downloadBlobAsPdf(pdfBlob, this.options.filename, contentType)
      }
      return pdfBlob
    } catch (error) {
      console.error('PDF生成エラー:', error)
      throw error
    }
  }

  /**
   * Rendering 対象となる HTML を返す
   */
  private async resolveHtmlContent(): Promise<string> {
    if (this.options.htmlContent) {
      return this.options.htmlContent
    }
    if (!this.options.htmlElement && !this.options.htmlElementSelector) {
      throw new Error(
        `Element with selector ${this.options.htmlElementSelector} not found`,
      )
    }
    let element =
      this.options.htmlElement || document.querySelector(this.options.htmlElementSelector)
    if (!element) {
      throw new Error(
        `Element with selector ${this.options.htmlElementSelector} not found`,
      )
    }
    element = element.cloneNode(true) as HTMLElement
    const { html, css } = await this.captureElement(element)
    const lineAwesomeFontCSS =
      this.options.disableLoadIconFont === true
        ? ''
        : await loadLineAwsomeFontCSSAsDataUrl()
    const zoomPart =
      this.options.zoom !== 1.0 ? `body { zoom: ${this.options.zoom} !important; }` : ''
    const cssPart = [lineAwesomeFontCSS, css, this.options.additionalStyles, zoomPart]
      .filter(Boolean)
      .map((part) => `<style>\n${part}\n</style>`)
      .join('\n')
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>PDF Export</title>
${cssPart}
</head>
<body>
${html}
</body>
</html>
`
  }

  /**
   * 特定の要素とそのスタイルを取得
   * @param {HTMLElement} element
   */
  private async captureElement(element: HTMLElement) {
    // スタイルの収集
    const styles: string[] = []

    // // 1. 要素に直接適用されているスタイル => TODO: これ???
    // const computedStyle = window.getComputedStyle(element)
    // const computedStyles: string[] = []
    // for (const prop of computedStyle) {
    //   const value = computedStyle.getPropertyValue(prop)
    //   if (value && prop.startsWith('--')) {
    //     computedStyles.push(`${prop}: ${value}`)
    //   }
    // }
    // if (computedStyles.length > 0) {
    //   styles.push(computedStyles.join('\n'))
    // }
    // console.log('[GotenbergClient] computedStyles:', computedStyles)
    // debugger

    // 2. CSSルールの収集
    try {
      for (const sheet of getStyleSheetsInDocumentOrder()) {
        // 同一オリジンのスタイルシートのみ処理
        if (!sheet.href || sheet.href.startsWith(window.location.origin)) {
          try {
            for (const rule of sheet.cssRules) {
              styles.push(rule.cssText)
            }
          } catch (e) {
            console.error('一部のスタイルシートの読み込みをスキップしました', e)
          }
        } else if (sheet.href.indexOf('line-awesome') > -1) {
          try {
            if (this.options.disableLoadIconFont === true) {
              continue
            }
            const cssText = await getCSSRulesFromExternalCSSUrl(sheet.href)
            if (cssText) {
              // @font-face { を削除 しつつ フォントを読み込む
              const cssTextWithoutFontFace = cssText.replace(/@font-face {[\s\S]*?}/g, '')
              styles.push(cssTextWithoutFontFace)
            }
          } catch (e) {
            console.warn('line-awesome のスタイルシートの読み込みに失敗しました', e)
          }
        } else {
          console.log('[GotenbergClient] skip sheet:', sheet.href)
        }
      }
    } catch (e) {
      console.warn('一部のスタイルシートの読み込みをスキップしました', e)
    }

    // 3. CSS変数の解決
    const cssVars = this._resolveCustomProperties()
    if (cssVars) {
      styles.unshift(cssVars) // CSS変数を先頭に追加
    }

    // 4. 画像の処理
    const processedElement = await this.processElementImages(element)

    // 5. すべての input の value を attr として付与するようにする
    const inputs = processedElement.querySelectorAll('input, textarea') as NodeListOf<
      HTMLInputElement | HTMLTextAreaElement
    >
    for (const input of inputs) {
      // input tag なら value を attr として付与する
      if (input.tagName === 'INPUT') {
        input.setAttribute('value', input.value)
      } else if (input.tagName === 'TEXTAREA') {
        // textarea tag なら innerText を 印刷用に表示
        // - テキストエリアのコンテンツをdivに置き換える
        input.style.display = 'none'
        const content = input.value
        const replacementDiv = document.createElement('div')
        replacementDiv.setAttribute('class', input.getAttribute('class') || '')
        replacementDiv.style.whiteSpace = 'pre-wrap'
        replacementDiv.style.wordWrap = 'break-word'
        replacementDiv.style.padding = input.style.padding || '5px'
        replacementDiv.style.width = '100%'
        replacementDiv.style.boxSizing = 'border-box'
        replacementDiv.style.minHeight = '1px'
        replacementDiv.textContent = content
        input.parentNode?.insertBefore(replacementDiv, input)
      }
    }

    // 6. その他 特殊 cases
    const autoHeightedElements = processedElement.querySelectorAll(
      '.auto-height-textarea-wrapper'
    ) as unknown as HTMLElement[]
    autoHeightedElements.forEach((wrapper) => {
      if (wrapper.style) {
        wrapper.style.height = 'auto'
      }
    })

    return {
      html: processedElement.outerHTML,
      css: styles.join('\n'),
    }
  }

  /**
   * CSS変数の解決
   */
  private _resolveCustomProperties() {
    const rootStyle = window.getComputedStyle(document.documentElement)
    const customProps = {}

    for (const prop of rootStyle) {
      if (prop.startsWith('--')) {
        customProps[prop] = rootStyle.getPropertyValue(prop).trim()
      }
    }

    if (Object.keys(customProps).length === 0) {
      return null
    }

    return `
:root {
  ${Object.entries(customProps)
    .map(([key, value]) => `${key}: ${value};`)
    .join('\n')}
}
`
  }

  /**
   * 要素内の画像をbase64エンコード
   * @param {HTMLElement} element
   */
  private async processElementImages(element: HTMLElement) {
    const images: HTMLImageElement[] = Array.from(element.getElementsByTagName('img'))

    await Promise.all(
      Array.from(images).map(async (img) => {
        if (img.src.startsWith('data:')) return

        try {
          const response = await fetch(img.src)
          const blob = await response.blob()
          const reader = new FileReader()
          await new Promise((resolve) => {
            reader.onload = () => {
              img.src = reader.result as string
              resolve(void 0)
            }
            reader.readAsDataURL(blob)
          })
        } catch (error) {
          console.warn(`画像の処理に失敗: ${img.src}`, error)
        }
      }),
    )

    return element
  }
}

const downloadBlobAsPdf = (
  blob: Blob,
  filename: string,
  contentType = 'application/pdf',
) => {
  // PDFのダウンロード
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  const fileExtension = contentType === 'application/zip' ? '.zip' : '.pdf'
  a.href = url
  a.download = `${filename}${filename.endsWith(fileExtension) ? '' : fileExtension}`

  // IEの場合の処理
  // @ts-ignore
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    // @ts-ignore
    window.navigator.msSaveOrOpenBlob(pdfBlob, 'document.pdf')
  } else {
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  window.URL.revokeObjectURL(url)
}

/**
 * ## htmlToPdf(): HTML を PDF に変換
 * - 指定された HTML 要素または CSS セレクターを PDF に変換します
 * - サーバ上の Chromium ブラウザを使用して PDF を生成するため、安定した PDF の出力が可能です
 * - 帳票出力などで使用します
 * - ダウンロードは自動で行われます (disableDownload を true にするとダウンロードを無効にできます)
 *
 * ### 利用例
 *
 * #### 基本例: PDF化する HTML Element を指定して PDF 化, ダウンロード
 *
 * ```ts
 * $core.$loading.start('PDF 出力中...', 'overlay')
 * try {
 *   // デフォルトで ダウンロード は 自動で行われます
 *   const pdfBlob = await $core.$utils.htmlToPdf({
 *     htmlElement: document.getElementById('elementId'),
 *     // htmlElement: this.$refs.targetElement, // Vuejs にて ref を指定 する場合
 *     // htmlElementSelector: '#elementId', // CSS セレクターを指定 する場合
 *     // paperWidth: '1080px', // PDF 化する場合の 用紙幅
 *     filename: '20240101_0001_請求一覧.pdf',
 *   })
 * } catch (error) {
 *   console.error('PDF 出力エラー:', error)
 *   $core.$toast.error('PDF生成時にエラーが発生しました')
 * } finally {
 *   $core.$loading.stop()
 * }
 * ```
 *
 * #### HTML 文字列を指定して PDF 化, ダウンロード
 *
 * ```ts
 * // CDN から Bootstrap を読み込んで利用する例
 * await $core.$utils.htmlToPdf({
 *   filename: '20240101_0001_請求一覧.pdf',
 *   htmlContent: `
 *   <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css">
 *   <div class="container">
 *     <h1>Hello, world!</h1>
 *     <p>PDF 出力テスト</p>
 *     <table class="table table-bordered">
 *       ...
 *     </table>
 *   </div>
 * `,
 * })
 * ```
 *
 * #### 各種オプションを指定
 *
 * ```ts
 * await $core.$utils.htmlToPdf({
 *   htmlElement: document.getElementById('elementId'),
 *   filename: '20240101_0001_請求一覧.pdf',
 *   disableDownload: true, // ダウンロードを無効にする
 *   header: '<div>header</div>', // ページヘッダー要素
 *   footer: '<div>footer</div>', // ページフッター要素
 *   additionalStyles: `
 *     .table-bordered {
 *       border: 1px solid #000;
 *     }
 *   `, // 追加のスタイル調整
 *   paperWidth: '1200px', // 用紙幅
 *   // paperHeight: '', // 用紙高さ
 *   marginTop: '15px', // 上マージン
 *   marginBottom: '15px', // 下マージン
 *   marginLeft: '15px', // 左マージン
 *   marginRight: '15px', // 右マージン
 *   preferCssPageSize: true, // CSS ページサイズを優先
 *   printBackground: true, // 背景グラフィックを印刷
 *   landscape: true, // 横向き印刷
 *   scale: 1.0, // 印刷スケール
 *   nativePageRanges: '1-5, 8, 11-13', // 印刷ページ範囲
 *   splitMode: 'pages', // 分割モード
 *   splitSpan: '1-5', // 分割範囲
 *   splitUnify: true, // 分割ページを統合
 *   pdfa: 'PDF/A-1b', // PDF/A 形式
 *   pdfua: true, // PDF/UA 形式
 *   metadata: { // ファイルのメタデータ
 *     title: 'PDF 出力テスト',
 *     author: 'PDF 出力テスト',
 *     subject: 'PDF 出力テスト',
 *     keywords: 'PDF 出力テスト',
 *     creator: 'PDF 出力テスト',
 *     producer: 'PDF 出力テスト',
 *   },
 * })
 * ```
 *
 * @returns PDF ファイルの Blob
 */
export const htmlToPdf = async (pdfOptions: HtmlToPdfOptions): Promise<Blob> => {
  if (
    !pdfOptions.htmlElement &&
    !pdfOptions.htmlContent &&
    !pdfOptions.htmlElementSelector
  ) {
    $core.$toast.error(
      'PDF 出力には HTML Element または CSS セレクター (htmlElement, htmlContent, htmlElementSelector) のいずれかを指定してください',
    )
    return
  }
  if (!pdfOptions.filename) {
    pdfOptions.filename = `document_${$core.$dayjs().format('YYYYMMDD_HHmmss')}.pdf`
  }

  const loadingMessage = 'PDF を生成しています...'
  // loading が 表示されていなければ 表示する
  if (!$core.$loading.state) {
    $core.$loading.start(loadingMessage, 'overlay')
  }
  try {
    const client = new GotenbergClient(pdfOptions)
    return await client.run()
  } catch (error) {
    console.error('PDF generation failed:', error)
    throw error
  } finally {
    if ($core.$loading.message === loadingMessage) {
      $core.$loading.stop()
    }
  }
}
